import React, { useEffect, useState } from 'react';
import {
  Badge, Box, Text, Center, Flex, Spinner, VStack, HStack,
  IconButton, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalCloseButton, useDisclosure, Divider, Avatar,
} from '@chakra-ui/react';
import { ViewIcon, CalendarIcon, TimeIcon } from "@chakra-ui/icons";
import MyDiv from './LeaveBalanceList.style';
import { CustomToast } from '../../../../components';
import { ILazyParams, useFetch } from '../../../../hooks/useFetch';
import CustomTables, { Column, ILazyParams as ICustomTableLazyParams } from '../../../../components/CustomTable/CustomTables';

interface LeavesBalanceProps {
  onView: (emp: any) => void;
  query: {
    lazyParams: ILazyParams;
    filter: Record<string, number[]>;
    search?: string | null;
  };
  setQuery: React.Dispatch<React.SetStateAction<{
    lazyParams: ILazyParams;
    filter: Record<string, number[]>;
    search?: string | null;
  }>>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

const getInitials = (name: string) =>
  name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "?";

// ── Leave Detail Modal ────────────────────────────────────────────────────────
const LeaveDetailModal = ({ row, isOpen, onClose }: { row: any; isOpen: boolean; onClose: () => void }) => {
  if (!row) return null;

  const leaveTypes = [
    { label: "Casual Leave", balance: row.balanceCL, used: row.usedCasualLeave, color: "blue", total: (row.balanceCL || 0) + (row.usedCasualLeave || 0) },
    { label: "Earned Leave", balance: row.balanceEL, used: row.usedEarnedLeave, color: "green", total: (row.balanceEL || 0) + (row.usedEarnedLeave || 0) },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
      <ModalContent borderRadius="20px" overflow="hidden" maxH="85vh">

        {/* Header */}
        <Box bg="#006838" px={6} pt={6} pb={5}>
          <ModalCloseButton color="white" top={4} right={4} />
          <Flex align="center" gap={4}>
            <Box>
              <Text color="white" fontWeight="700" fontSize="17px" lineHeight={1.2}>{row.employeeName}</Text>
              <HStack spacing={2} mt={1}>
                <Badge bg="whiteAlpha.300" color="white" fontSize="11px" borderRadius="full" px={2}>{row.employee_Code}</Badge>
              </HStack>
            </Box>
          </Flex>

        </Box>

        <ModalBody px={6} py={5}>
          <Text fontWeight="700" fontSize="14px" color="gray.700" mb={4} letterSpacing="0.03em" textTransform="uppercase">
            Leave History
          </Text>

          {row.leaveDates && row.leaveDates.length > 0 ? (
            <VStack spacing={3} align="stretch">
              {row.leaveDates.map((leave: any, i: number) => (
                <Box key={i} border="1px solid" borderColor={leave.isSandwich ? "orange.200" : "gray.200"} borderRadius="12px" overflow="hidden" bg={leave.isSandwich ? "orange.50" : "white"}>
                  <Box h="3px" bg={leave.isSandwich ? "orange.400" : "#006838"} />

                  <Box px={4} py={3}>
                    <Flex justify="space-between" align="center" mb={3}>
                      <HStack spacing={2}>
                        <Badge colorScheme={leave.isSandwich ? "orange" : "green"} borderRadius="full" px={3} py={0.5} fontSize="11px" fontWeight="600"                         >
                          {leave.totalDays} {leave.totalDays === 1 ? "Day" : "Days"}
                        </Badge>
                        {leave.isSandwich && (
                          <Badge colorScheme="orange" variant="outline" borderRadius="full" px={2} fontSize="10px">Sandwich                          </Badge>
                        )}
                      </HStack>
                      <Badge colorScheme={leave.leaveType === "CL" ? "blue" : "purple"} variant="subtle" borderRadius="full" px={2} fontSize="10px" >
                        {leave.leaveType || "Leave"}
                      </Badge>
                    </Flex>

                    <Flex align="center" gap={0} justify="space-between">
                      <Box flex={1}>
                        <Text fontSize="10px" color="gray.400" fontWeight="500" textTransform="uppercase" letterSpacing="0.05em" mb={0.5}>From</Text>
                        <Text fontSize="14px" fontWeight="700" color="gray.800">{fmtDate(leave.startDate)}</Text>
                      </Box>

                      {/* Arrow */}
                      <Flex direction="column" align="center" px={3}>
                        <Box h="1px" w="40px" bg="gray.300" position="relative">
                          <Box
                            position="absolute" right={0} top="-3px"
                            w={0} h={0}
                            borderTop="4px solid transparent"
                            borderBottom="4px solid transparent"
                            borderLeft="6px solid"
                            borderLeftColor="gray.300"
                          />
                        </Box>
                      </Flex>

                      <Box flex={1} textAlign="right">
                        <Text fontSize="10px" color="gray.400" fontWeight="500" textTransform="uppercase" letterSpacing="0.05em" mb={0.5}>
                          To
                        </Text>
                        <Text fontSize="14px" fontWeight="700" color="gray.800">
                          {fmtDate(leave.endDate)}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Box>
              ))}
            </VStack>
          ) : (
            <Flex direction="column" align="center" justify="center" py={10} gap={3}>
              <CalendarIcon color="gray.300" boxSize={8} />
              <Text fontSize="sm" color="gray.400" fontWeight="500">No leave records found</Text>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const LeaveBalanceList: React.FC<LeavesBalanceProps> = ({ onView, query, setQuery }) => {
  const { addToast } = CustomToast();
  const { fetchApi, loading } = useFetch(addToast);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [leaveBalance, setLeavBalance] = useState<{ data: any[]; totalCount: number }>({ data: [], totalCount: 0, });
  const [lazyParams, setLazyParams] = useState<ICustomTableLazyParams>({
    first: 0,
    rows: 20,
    sortField: undefined,
    sortOrder: undefined,
    multiSortMeta: [],
    filters: {},
    globalFilter: "",
  });


  const loadBalanceLeave = async () => {
    const params = new URLSearchParams();

    params.set("first", String(lazyParams.first));
    params.set("rows", String(lazyParams.rows));

    if (lazyParams.sortField) {
      params.set("sortField", lazyParams.sortField);
    }

    if (lazyParams.sortOrder) {
      params.set("sortOrder", String(lazyParams.sortOrder));
    }

    if (lazyParams.globalFilter) {
      params.set("globalFilter", lazyParams.globalFilter);
    }

    if (lazyParams.filters && Object.keys(lazyParams.filters).length) {
      params.set("filters", JSON.stringify(lazyParams.filters));
    }

    const response = await fetchApi( "Hrlense_BalanceLeave", "GET", "", params.toString(), "" );

    if (response) { setLeavBalance({ data: response.balanceLeaves ?? [], totalCount: response.totalCount ?? 0, });
    }
  };

useEffect(() => {
  loadBalanceLeave();
}, [lazyParams]);

  const openDetail = (row: any) => {
    setSelectedRow(row);
    onOpen();
  };


  return (
    <MyDiv>
      <Box position="relative" minH="300px">
        <CustomTables<any>
          value={leaveBalance.data} loading={loading} emptyMessage="No Leave Balance Data Available" preferenceKey="leaveBalanceTable" showToolbar={true}
          showGlobalSearch={false} lazy lazyParams={lazyParams} onLazyParamsChange={setLazyParams} totalRecords={leaveBalance.totalCount}
          showPagination rowsPerPageOptions={[20, 50, 100, 500]} maxTableHeight={null}
        >
          <Column header="S.No" body={(_: any, index: number) => (<Text fontSize="13px" color="gray.500" fontWeight="500">{query.lazyParams.first + index + 1}</Text>)} />
          <Column header="Emp. Code" field="employee_Code" sortable filterable filterType="number" body={(row: any) => (
              <Badge colorScheme="green" variant="subtle" borderRadius="8px" px={2} py={1} fontSize="12px" fontFamily="mono" cursor="pointer" fontWeight="700" _hover={{ bg: "green.100" }} onClick={() => onView(row)}  >
                {row.employee_Code}
              </Badge>
            )}
          />

          <Column header="Name" field="employeeName" sortable filterable filterType="text" />
          <Column header="Casual Leave" field="balanceCL" sortable   />
          <Column header="Earned Leave" field="balanceEL" sortable   />
          <Column header="Used CL" field="usedCasualLeave" sortable />
          <Column header="Used EL" field="usedEarnedLeave" sortable  />
          <Column header="Sandwich" field="sandwichLeave" sortable   />

          <Column header="Total Used" field="totalLeave" sortable  body={(row: any) => (
            <Box bg={row.totalLeave > 15 ? "red.50" : row.totalLeave > 8 ? "orange.50" : "green.50"} borderRadius="8px" px={3} py={1.5} display="inline-block" >
              <Text fontSize="14px" fontWeight="700" color={row.totalLeave > 15 ? "red.600" : row.totalLeave > 8 ? "orange.600" : "green.700"} >
                {row.totalLeave}
              </Text>
            </Box>
          )} />
          <Column header="Details" frozen="right" body={(row: any) => (
            <IconButton aria-label="View leave details" icon={<ViewIcon />} size="sm" bg="#006838" color="white" borderRadius="8px" _hover={{ bg: "#005530" }} onClick={() => openDetail(row)} />
          )} />
        </CustomTables>
      </Box>

      <LeaveDetailModal row={selectedRow} isOpen={isOpen} onClose={onClose} />
    </MyDiv>
  );
};

export default LeaveBalanceList;